import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import type { ThemeMode, ThemeContextValue } from "./types";
import {
  colorSchemes,
  DEFAULT_COLOR_SCHEME_ID,
  STORAGE_KEY_MODE,
  STORAGE_KEY_SCHEME,
  readThemeCookieMode,
  writeThemeCookie,
} from "./theme-config";

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemPreference(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function subscribeToMediaQuery(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "auto";
  // The server-readable `theme` cookie is authoritative: the no-FOUC pre-paint
  // script already applied it before React mounted, so hydrating from it keeps
  // in-app state in lockstep with the painted theme (no flash, GAP-01). Fall
  // back to localStorage, then system, only when the cookie is absent.
  const cookieMode = readThemeCookieMode();
  if (cookieMode) return cookieMode;
  const stored = localStorage.getItem(STORAGE_KEY_MODE);
  if (stored === "light" || stored === "dark" || stored === "auto") {
    return stored;
  }
  return "auto";
}

function readStoredScheme(): string {
  if (typeof window === "undefined") return DEFAULT_COLOR_SCHEME_ID;
  return localStorage.getItem(STORAGE_KEY_SCHEME) ?? DEFAULT_COLOR_SCHEME_ID;
}

/**
 * Selects the color scheme via a `data-scheme` attribute on <html> (A3): the
 * generated `[data-scheme="…"]` blocks in styles/schemes.css carry the values,
 * so no inline style injection is needed. The default (trust) scheme lives in
 * :root/.dark and renders with NO attribute — the SSR/no-JS path. Unknown ids
 * (including the legacy stored "default") resolve to trust upstream, so this
 * removes the attribute for them too.
 */
function applySchemeAttribute(schemeId: string) {
  const root = document.documentElement;
  if (schemeId === DEFAULT_COLOR_SCHEME_ID) {
    delete root.dataset.scheme;
  } else {
    root.dataset.scheme = schemeId;
  }
}

function applyDarkClass(isDark: boolean) {
  document.documentElement.classList.toggle("dark", isDark);
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  defaultColorSchemeId?: string;
}

export function ThemeProvider({ children, defaultMode, defaultColorSchemeId }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => defaultMode ?? readStoredMode());
  const [colorSchemeId, setColorSchemeIdState] = useState(
    () => defaultColorSchemeId ?? readStoredScheme(),
  );

  const systemPreference = useSyncExternalStore(
    subscribeToMediaQuery,
    getSystemPreference,
    () => "light" as const,
  );

  const resolvedMode = mode === "auto" ? systemPreference : mode;

  const scheme = useMemo(() => {
    const resolved =
      colorSchemes.find((s) => s.id === colorSchemeId) ??
      colorSchemes.find((s) => s.id === DEFAULT_COLOR_SCHEME_ID) ??
      colorSchemes[0];
    // colorSchemes is a non-empty module constant; this satisfies the type
    // checker under noUncheckedIndexedAccess without a runtime path change.
    if (!resolved) throw new Error("colorSchemes must not be empty");
    return resolved;
  }, [colorSchemeId]);

  // Apply theme to DOM: scheme via the data-scheme attribute (A3), mode via .dark.
  useEffect(() => {
    applySchemeAttribute(scheme.id);
    applyDarkClass(resolvedMode === "dark");
  }, [resolvedMode, scheme]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    // Cookie is the authoritative, server-readable persistence path the no-FOUC
    // script reads pre-paint (GAP-01); localStorage is kept for back-compat.
    writeThemeCookie(newMode);
    localStorage.setItem(STORAGE_KEY_MODE, newMode);
  }, []);

  const setColorScheme = useCallback((id: string) => {
    setColorSchemeIdState(id);
    localStorage.setItem(STORAGE_KEY_SCHEME, id);
  }, []);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedMode,
      colorSchemeId,
      setMode,
      setColorScheme,
      colorSchemes,
    }),
    [mode, resolvedMode, colorSchemeId, setMode, setColorScheme],
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}
